/**
 * Utility functions for parsing and structuring Swagger/OpenAPI documentation
 */

/**
 * Parse markdown/HTML content to extract headers and sections
 * @param {string} description - The description text to parse
 * @returns {Array} Array of section objects
 */
export const parseDescription = (description) => {
  if (!description) return [];

  const sections = [];
  const lines = description.split("\n");
  let currentSection = null;
  let currentContent = [];
  let foundEntitiesHeader = false;
  let skipUntilNextHeader = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for markdown headers
    const h1Match = line.match(/^#\s+(.+)$/);
    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);

    // Check for HTML headers
    const htmlH1Match = line.match(/<h1[^>]*>(.+?)<\/h1>/i);
    const htmlH2Match = line.match(/<h2[^>]*>(.+?)<\/h2>/i);
    const htmlH3Match = line.match(/<h3[^>]*>(.+?)<\/h3>/i);

    let headerLevel = 0;
    let headerText = null;

    if (h1Match || htmlH1Match) {
      headerLevel = 1;
      headerText = h1Match ? h1Match[1] : htmlH1Match[1];

      // Check if this is the "Entities" header
      if (headerText.toLowerCase() === "entities") {
        foundEntitiesHeader = true;
        skipUntilNextHeader = true;
        continue; // Skip this header
      } else {
        skipUntilNextHeader = false;
      }
    } else if (h2Match || htmlH2Match) {
      headerLevel = 2;
      headerText = h2Match ? h2Match[1] : htmlH2Match[1];
    } else if (h3Match || htmlH3Match) {
      headerLevel = 3;
      headerText = h3Match ? h3Match[1] : htmlH3Match[1];
    }

    // Skip content if we're in the Entities section
    if (skipUntilNextHeader && headerLevel === 0) {
      continue;
    }

    if (headerText) {
      // Save previous section if exists and has meaningful content
      if (currentSection) {
        currentSection.content = currentContent.join("\n").trim();
        // Only add section if it has meaningful content (using hasContent helper)
        if (hasContent(currentSection.content)) {
          sections.push(currentSection);
        }
      }

      // Start new section
      currentSection = {
        id: `section-${sections.length}`,
        title: headerText.trim(),
        level: headerLevel,
        type: "documentation",
        content: "",
      };
      currentContent = [];
    } else if (!skipUntilNextHeader) {
      currentContent.push(line);
    }
  }

  // Save last section
  if (currentSection) {
    currentSection.content = currentContent.join("\n").trim();
    // Only add section if it has meaningful content
    if (hasContent(currentSection.content)) {
      sections.push(currentSection);
    }
  }

  return sections;
};

/**
 * Extract endpoint groups and endpoints from paths
 * @param {Object} paths - The paths object from swagger
 * @returns {Array} Array of endpoint groups
 */
export const extractEndpoints = (paths) => {
  if (!paths) return [];

  const groups = {};

  Object.entries(paths).forEach(([path, methods]) => {
    // Try to extract group from path (e.g., /users/xxx -> users)
    const pathParts = path.split("/").filter((p) => p && !p.includes("{"));
    const groupName = pathParts[0] || "General";

    if (!groups[groupName]) {
      groups[groupName] = {
        id: `group-${groupName}`,
        title: groupName.charAt(0).toUpperCase() + groupName.slice(1),
        type: "endpoint-group",
        children: [], // Changed from 'endpoints' to 'children' for consistency
      };
    }

    Object.entries(methods).forEach(([method, spec]) => {
      if (
        ["get", "post", "put", "delete", "patch", "options", "head"].includes(
          method.toLowerCase()
        )
      ) {
        groups[groupName].children.push({
          id: `endpoint-${method}-${path}`.replace(/[^a-zA-Z0-9-]/g, "-"),
          method: method.toUpperCase(),
          path,
          summary: spec.summary || "",
          description: spec.description || "",
          operationId: spec.operationId,
          type: "endpoint",
          // Include the full endpoint data for the content renderer
          parameters: spec.parameters,
          requestBody: spec.requestBody,
          responses: spec.responses,
          tags: spec.tags,
          security: spec.security,
        });
      }
    });
  });

  return Object.values(groups);
};

/**
 * Extract entities from components.schemas
 * @param {Object} schemas - The schemas object from components
 * @returns {Array} Array of entity objects
 */
export const extractEntities = (schemas) => {
  if (!schemas) return [];

  return Object.entries(schemas).map(([name, schema]) => ({
    id: `entity-${name}`,
    title: name,
    type: "entity",
    schema,
    required: schema.required || [],
    properties: schema.properties || {},
  }));
};

/**
 * Extract security schemes from components
 * @param {Object} securitySchemes - The securitySchemes object from components
 * @returns {Array} Array of security scheme objects
 */
export const extractSecuritySchemes = (securitySchemes) => {
  if (!securitySchemes) return [];

  return Object.entries(securitySchemes).map(([name, scheme]) => ({
    id: `security-${name}`,
    title: name,
    type: "security",
    scheme,
  }));
};

/**
 * Check if content is meaningful (not empty or just whitespace)
 * @param {*} content - The content to check
 * @returns {boolean} True if content has meaningful data
 */
const hasContent = (content) => {
  if (!content) return false;

  // For strings, check if not just whitespace or HTML comments
  if (typeof content === "string") {
    const trimmed = content.trim();

    // Check if empty
    if (trimmed.length === 0) return false;

    // Check if only contains HTML comments
    const withoutComments = trimmed.replace(/<!--[\s\S]*?-->/g, "").trim();
    if (withoutComments.length === 0) return false;

    // Check if only contains ReDoc inject markers (common in API docs)
    if (/^<!--\s*ReDoc-Inject:.*-->$/i.test(trimmed)) return false;

    return true;
  }

  // For objects, check if not empty
  if (typeof content === "object") {
    if (Array.isArray(content)) {
      return content.length > 0;
    }
    return Object.keys(content).length > 0;
  }

  return true;
};

/**
 * Build complete table of contents from swagger data
 * @param {Object} swaggerData - The complete swagger JSON object
 * @returns {Array} Structured table of contents
 */
export const buildTableOfContents = (swaggerData) => {
  if (!swaggerData) return [];

  const toc = [];

  // 1. Add general info section if it has content
  if (swaggerData.info && hasContent(swaggerData.info)) {
    toc.push({
      id: "info",
      title: swaggerData.info.title || "API Documentation",
      type: "info",
      version: swaggerData.info.version,
      content: swaggerData.info,
    });
  }

  // 2. Parse and add documentation sections from description
  if (
    swaggerData.info?.description &&
    hasContent(swaggerData.info.description)
  ) {
    const docSections = parseDescription(swaggerData.info.description);
    // Filter out empty sections
    const nonEmptyDocSections = docSections.filter((section) =>
      hasContent(section.content)
    );

    // Always add Documentation with the full description content if it has content
    if (nonEmptyDocSections.length > 0) {
      toc.push({
        id: "documentation",
        title: "Documentation",
        type: "documentation",
        content: swaggerData.info.description,
        children: nonEmptyDocSections,
      });
    } else if (hasContent(swaggerData.info.description)) {
      // Add the entire description as a single documentation item
      toc.push({
        id: "documentation",
        title: "Documentation",
        type: "documentation",
        content: swaggerData.info.description,
      });
    }
  }

  // 3. Add endpoints
  if (swaggerData.paths && hasContent(swaggerData.paths)) {
    const endpointGroups = extractEndpoints(swaggerData.paths);
    // Filter out groups with no children
    const nonEmptyGroups = endpointGroups.filter(
      (group) => group.children && group.children.length > 0
    );

    if (nonEmptyGroups.length > 0) {
      toc.push({
        id: "endpoints",
        title: "API Endpoints",
        type: "section-group",
        children: nonEmptyGroups,
      });
    }
  }

  // 4. Add entities/schemas
  if (
    swaggerData.components?.schemas &&
    hasContent(swaggerData.components.schemas)
  ) {
    const entities = extractEntities(swaggerData.components.schemas);
    // Filter out empty entities
    const nonEmptyEntities = entities.filter(
      (entity) => hasContent(entity.properties) || hasContent(entity.schema)
    );

    if (nonEmptyEntities.length > 0) {
      toc.push({
        id: "entities",
        title: "Data Models",
        type: "section-group",
        children: nonEmptyEntities,
      });
    }
  }

  // 5. Add security schemes
  if (
    swaggerData.components?.securitySchemes &&
    hasContent(swaggerData.components.securitySchemes)
  ) {
    const securitySchemes = extractSecuritySchemes(
      swaggerData.components.securitySchemes
    );
    // Filter out empty schemes
    const nonEmptySchemes = securitySchemes.filter((scheme) =>
      hasContent(scheme.scheme)
    );

    if (nonEmptySchemes.length > 0) {
      toc.push({
        id: "security",
        title: "Authentication",
        type: "section-group",
        children: nonEmptySchemes,
      });
    }
  }

  return toc;
};

/**
 * Detect content type (HTML, Markdown, JSON, etc.)
 * @param {string} content - The content to analyze
 * @returns {string} The detected content type
 */
export const detectContentType = (content) => {
  if (!content || typeof content !== "string") {
    if (typeof content === "object") return "json";
    return "text";
  }

  // Check for HTML
  if (/<[^>]+>/.test(content)) {
    return "html";
  }

  // Check for Markdown indicators
  if (
    /^#{1,6}\s+.+$/m.test(content) ||
    /\[.+\]\(.+\)/.test(content) ||
    /```[\s\S]*```/.test(content)
  ) {
    return "markdown";
  }

  // Try to parse as JSON
  try {
    JSON.parse(content);
    return "json";
  } catch {
    // Not JSON
  }

  return "text";
};
